// Bulk proposal parsing, validation and assembly. Mostly pure (chunk store uses
// localStorage). A "bulk" proposal describes many events compactly via
// recurrence rules and/or a bounded explicit `events` list, then this module
// expands + validates them into one coherent, safe proposal object.

import { CATEGORY_MAP } from "./categories";
import { expandRecurrence } from "./recurrence";
import { BULK_CONFIG } from "./bulkConfig";

export const BULK_FENCE_RE = /```chronicles-bulk\s*([\s\S]*?)```/;

function sanitizeJson(s) {
  return s
    .replace(/\/\*[\s\S]*?\*\//g, "")
    .replace(/(^|[^:])\/\/[^\n\r]*/g, "$1")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/,\s*([}\]])/g, "$1");
}

export function tryParse(raw) {
  try { return JSON.parse(raw); } catch {
    try { return JSON.parse(sanitizeJson(raw)); } catch { return null; }
  }
}

export function makeId(prefix = "bulk") {
  const rnd =
    (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
    `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}_${rnd}`;
}

// Extract a compact bulk block from assistant text. Returns { visibleText, bulk|null }.
export function extractBulkPayload(rawText) {
  const match = (rawText || "").match(BULK_FENCE_RE);
  if (!match) return { visibleText: rawText, bulk: null };
  const parsed = tryParse(match[1].trim());
  const bulk = parsed && parsed.bulk && typeof parsed.bulk === "object" ? parsed.bulk : null;
  const visibleText = rawText.replace(BULK_FENCE_RE, "").trim();
  return { visibleText, bulk };
}

function validateEventDraft(d) {
  if (!d || typeof d !== "object") return "malformed";
  if (!d.title || !String(d.title).trim()) return "missing title";
  if (typeof d.date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(d.date)) return "invalid date";
  if (Number.isNaN(new Date(`${d.date}T00:00:00`).getTime())) return "invalid date";
  if (!CATEGORY_MAP[d.category]) return "invalid category";
  const s = (d.startHour ?? 0) * 60 + (d.startMinute ?? 0);
  const e = (d.endHour ?? 0) * 60 + (d.endMinute ?? 0);
  if (!(e > s)) return "end must be after start";
  return null;
}

function contentKey(d) {
  return `${d.title}|${d.date}|${d.startHour}:${d.startMinute}|${d.endHour}:${d.endMinute}`;
}

function overlaps(a, b) {
  const as = a.startHour * 60 + a.startMinute;
  const ae = a.endHour * 60 + a.endMinute;
  const bs = b.startHour * 60 + b.startMinute;
  const be = b.endHour * 60 + b.endMinute;
  return as < be && bs < ae;
}

// Count conflicts (overlaps) among the new events and against existing events,
// grouped by date to stay efficient for large sets.
function countConflicts(drafts, existingEvents) {
  const byDate = new Map();
  for (const e of existingEvents || []) {
    if (!byDate.has(e.date)) byDate.set(e.date, []);
    byDate.get(e.date).push(e);
  }
  let conflicts = 0;
  for (const d of drafts) {
    const day = byDate.get(d.date) || [];
    for (const other of day) {
      if (overlaps(d, other)) { conflicts += 1; break; }
    }
    if (!byDate.has(d.date)) byDate.set(d.date, []);
    byDate.get(d.date).push(d);
  }
  return conflicts;
}

/**
 * Build a validated bulk proposal from a compact payload.
 * payload = { title, dateRange:{start,end}, recurrences:[...], events:[...] }
 * Returns a proposal object (never throws) with full validation status.
 */
export function buildBulkProposal(payload, existingEvents = [], config = BULK_CONFIG, opts = {}) {
  const proposalId = opts.proposalId || makeId();
  const cap = config.maxEventsPerJob;
  const drafts = [];
  const errors = [];
  let truncatedByCap = false;

  const recurrences = Array.isArray(payload?.recurrences) ? payload.recurrences : [];
  for (let ri = 0; ri < recurrences.length; ri += 1) {
    if (drafts.length >= cap) { truncatedByCap = true; break; }
    const remaining = cap - drafts.length;
    const { events, error, truncated } = expandRecurrence(recurrences[ri], remaining, config.maxDateRangeDays);
    if (error) errors.push({ scope: `recurrence[${ri}]`, reason: error });
    if (truncated) truncatedByCap = true;
    for (const ev of events) drafts.push(ev);
  }

  const explicit = Array.isArray(payload?.events) ? payload.events : [];
  for (const ev of explicit) {
    if (drafts.length >= cap) { truncatedByCap = true; break; }
    drafts.push(ev);
  }

  // Validate + dedupe.
  const seen = new Set();
  const valid = [];
  const invalidItems = [];
  let skippedDuplicates = 0;
  drafts.forEach((d, idx) => {
    const err = validateEventDraft(d);
    if (err) { invalidItems.push({ index: idx, title: d && d.title, reason: err }); return; }
    const key = contentKey(d);
    if (seen.has(key)) { skippedDuplicates += 1; return; }
    seen.add(key);
    valid.push({
      ...d,
      startMinute: d.startMinute || 0,
      endMinute: d.endMinute || 0,
      priority: d.priority ?? 3,
      description: d.description || "",
      status: d.status || "planned",
      sourceEventId: `${proposalId}#${valid.length}`,
    });
  });

  const categorySummary = {};
  for (const d of valid) categorySummary[d.category] = (categorySummary[d.category] || 0) + 1;

  const dates = valid.map((d) => d.date).sort();
  const dateRange = payload?.dateRange || {
    start: dates[0] || null,
    end: dates[dates.length - 1] || null,
  };

  const conflictCount = countConflicts(valid, existingEvents);

  const overLimit = valid.length > cap;
  const validationStatus = {
    valid: invalidItems.length === 0 && errors.length === 0 && !overLimit,
    invalidCount: invalidItems.length,
    invalidItems: invalidItems.slice(0, 50),
    ruleErrors: errors,
    skippedDuplicates,
    truncatedByCap,
    overLimit,
  };

  return {
    proposalId,
    kind: "bulk_create_events",
    title: (payload && payload.title) || "Bulk calendar plan",
    userId: null, // ownership enforced by scoped storage + backend auth
    dateRange,
    estimatedEventCount: valid.length,
    categorySummary,
    conflictSummary: { conflictCount },
    events: valid,
    previewEvents: valid.slice(0, config.previewCount),
    validationStatus,
    creationStatus: "pending",
    createdAt: new Date().toISOString(),
  };
}

/* ---------------- Chunk continuation (non-recurring large lists) ----------------
 * A draft is assembled from ordered chunks keyed by bulkDraftId. Each chunk is
 * validated independently; malformed chunks are rejected while valid ones are
 * retained, and missing/duplicate chunks are detected before a proposal is built.
 */

const DRAFTS_KEY = "chronicles.bulkDrafts.v1";

function readDrafts() {
  try {
    const raw = localStorage.getItem(DRAFTS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch { return {}; }
}
function writeDrafts(obj) {
  localStorage.setItem(DRAFTS_KEY, JSON.stringify(obj));
}

// Add/replace a single validated chunk. chunk = { bulkDraftId, chunkIndex, totalChunks, events }
// Returns { ok, reason }. A malformed chunk is rejected without touching stored chunks.
export function addBulkChunk(chunk, config = BULK_CONFIG) {
  if (!chunk || !chunk.bulkDraftId || !Number.isInteger(chunk.chunkIndex) || !Number.isInteger(chunk.totalChunks)) {
    return { ok: false, reason: "malformed chunk header" };
  }
  if (chunk.totalChunks > config.maxChunksPerProposal) {
    return { ok: false, reason: "too many chunks" };
  }
  if (!Array.isArray(chunk.events)) return { ok: false, reason: "malformed chunk events" };

  const drafts = readDrafts();
  const now = Date.now();
  const d = drafts[chunk.bulkDraftId] || {
    bulkDraftId: chunk.bulkDraftId,
    totalChunks: chunk.totalChunks,
    chunks: {},
    createdAt: now,
  };
  d.totalChunks = chunk.totalChunks;
  d.chunks[String(chunk.chunkIndex)] = chunk.events; // duplicate index safely overwrites (idempotent)
  d.updatedAt = now;
  drafts[chunk.bulkDraftId] = d;
  writeDrafts(drafts);
  return { ok: true, reason: null };
}

// Inspect a draft's completeness. Returns { complete, present, missing:[...], expired }.
export function bulkDraftStatus(bulkDraftId, config = BULK_CONFIG) {
  const d = readDrafts()[bulkDraftId];
  if (!d) return { complete: false, present: 0, missing: [], expired: false, total: 0 };
  const expired = Date.now() - (d.createdAt || 0) > config.chunkExpiryMs;
  const present = Object.keys(d.chunks).length;
  const missing = [];
  for (let i = 0; i < d.totalChunks; i += 1) {
    if (!(String(i) in d.chunks)) missing.push(i);
  }
  return { complete: missing.length === 0, present, missing, expired, total: d.totalChunks };
}

// Assemble a completed draft into ONE bulk proposal (ordered by chunk index).
export function assembleBulkDraft(bulkDraftId, existingEvents = [], config = BULK_CONFIG, opts = {}) {
  const status = bulkDraftStatus(bulkDraftId, config);
  if (status.expired) return { ok: false, reason: "expired", status };
  if (!status.complete) return { ok: false, reason: "incomplete", status };
  const d = readDrafts()[bulkDraftId];
  const events = [];
  for (let i = 0; i < d.totalChunks; i += 1) {
    for (const ev of d.chunks[String(i)]) events.push(ev);
  }
  const proposal = buildBulkProposal({ ...opts.payload, events }, existingEvents, config, opts);
  return { ok: true, proposal, status };
}

export function clearBulkDraft(bulkDraftId) {
  const drafts = readDrafts();
  delete drafts[bulkDraftId];
  writeDrafts(drafts);
}
